import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  submitted: "#8A8A82",
  under_review: "#D8B23E",
  approved: "#2FB999",
  accepted: "#2FB999",
  paid: "#2FB999",
  rejected: "#C0392B",
  open: "#5B8DEF",
  contacted: "#D8B23E",
  quoted: "#B87333",
  closed: "#8A8A82",
};

export const StatusPill = ({ status, testid }) => {
  const color = STATUS_COLORS[status] || "#8A8A82";
  const label = String(status || "").replace(/_/g, " ");
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 border"
      style={{ color, borderColor: `${color}55`, background: `${color}12` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
};

export const AccountShell = ({ kicker, title, subtitle, children }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]">
      <div className="border-b border-white/10 sticky top-0 z-40 bg-black/70 backdrop-blur-xl">
        <div className="max-w-[1100px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Vinci" className="h-7 w-auto" />
            <span className="font-serif text-xl">Vinci Inc.</span>
          </Link>
          <button
            data-testid="back-to-account"
            onClick={() => nav("/account")}
            className="inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors"
          >
            <ArrowLeft size={14} /> Account
          </button>
        </div>
      </div>
      <div className="max-w-[1100px] mx-auto px-6 py-14 md:py-16">
        <span className="overline text-[#B87333]">{kicker}</span>
        <h1 className="font-serif text-4xl md:text-6xl mt-4 tracking-[-0.02em]" data-testid="page-title">{title}</h1>
        {subtitle && <p className="font-body text-[#8A8A82] mt-4 max-w-2xl leading-relaxed">{subtitle}</p>}
        {user && user.email && (
          <p className="font-body text-xs text-[#6f6f68] mt-2">Signed in as {user.email}</p>
        )}
        <div className="mt-12">{children}</div>
      </div>
    </div>
  );
};

export const FileUpload = ({ testid, onUploaded, doc }) => {
  const { api } = useAuth();
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data);
    } catch {
      onUploaded(null, "Upload failed. Try a smaller file (max 15MB).");
    }
  };
  return (
    <label className="block group cursor-pointer">
      <span className="overline text-[#8A8A82]">Supporting document (optional)</span>
      <div className="mt-2 flex items-center gap-3 border-b border-white/15 py-3">
        <span className="rounded-full border border-white/20 px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">Choose file</span>
        <span className="font-body text-sm text-[#C9C9C2] truncate">{doc?.name || "PDF, image or spreadsheet"}</span>
        <input data-testid={testid} type="file" onChange={handle} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" />
      </div>
    </label>
  );
};
